import { RiUploadCloud2Line, RiCloseLine, RiMapPinLine } from 'react-icons/ri'
import TypeGuide from '../../../components/TypeGuide.jsx'
import { Btn, Select, Field, Toggle, Modal } from '../../../components/ui/design.jsx'

export default function ImageUploadModal({ open, onClose, types, onUpload, uploadHook }) {
  if (!open) return null

  const {
    uploadForm, setUploadForm,
    uploadFiles, addFiles, removeFile, clearAll,
    uploadProgress, dragOver, setDragOver,
    onUpload: handleSubmit,
  } = uploadHook

  const selectedType = types.find((t) => String(t.id) === String(uploadForm.image_type_id))

  return (
    <Modal onClose={onClose} title="Unggah Gambar" wide>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type selector */}
        <Field label="Tipe gambar" req>
          <Select
            required
            value={uploadForm.image_type_id}
            onChange={(e) => setUploadForm({ ...uploadForm, image_type_id: e.target.value })}
          >
            <option value="">Pilih tipe...</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.is_unlimited ? '' : `(${t.active_images_count}/${t.max_active} aktif)`}
              </option>
            ))}
          </Select>
        </Field>

        {/* TypeGuide */}
        {selectedType && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-gray-500">
              <RiMapPinLine className="text-blue-500" /> Gambar ini akan tampil di:
            </p>
            <TypeGuide typeSlug={selectedType.slug} />
          </div>
        )}

        {/* Drag-drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
          onClick={() => document.getElementById('file-input-multi')?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center transition-all ${
            dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <input id="file-input-multi" type="file" accept="image/*" multiple onChange={(e) => addFiles(e.target.files)} className="sr-only" />
          <RiUploadCloud2Line className="mb-2 text-4xl text-gray-300" />
          <p className="text-sm font-semibold text-gray-700">Seret & letakkan gambar di sini</p>
          <p className="mt-1 text-xs text-gray-400">atau klik untuk memilih file — bisa lebih dari satu</p>
        </div>

        {/* File preview grid */}
        {uploadFiles.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">{uploadFiles.length} gambar dipilih</span>
              <button type="button" onClick={clearAll} className="text-xs text-red-500 hover:text-red-700">
                Hapus semua
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 max-h-[40vh] overflow-y-auto">
              {uploadFiles.map((entry, i) => (
                <div key={`${entry.file.name}-${i}`} className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="aspect-square">
                    <img src={entry.preview} alt={entry.file.name} className="h-full w-full object-cover" loading="lazy" />
                  </div>

                  {/* Status badge */}
                  {entry.status !== 'pending' && (
                    <span className={`absolute left-2 top-2 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                      entry.status === 'done'
                        ? 'bg-emerald-500 text-white'
                        : entry.status === 'error'
                          ? 'bg-red-500 text-white'
                          : 'bg-slate-800 text-white animate-pulse'
                    }`}>
                      {entry.status === 'done' ? '✓' : entry.status === 'error' ? '✗' : '…'}
                    </span>
                  )}

                  {/* Remove button (only pending) */}
                  {entry.status === 'pending' && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeFile(i) }}
                      className="absolute right-1.5 top-1.5 rounded-md bg-black/50 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <RiCloseLine />
                    </button>
                  )}

                  {/* Title input + error */}
                  <div className="p-1.5">
                    <input
                      value={entry.title}
                      onChange={(e) => uploadHook.updateFile?.(i, { title: e.target.value }) ?? (() => {
                        // Fallback if updateFile is not passed via hook
                      })()}
                      placeholder="Judul"
                      className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] outline-none focus:border-blue-500"
                    />
                    {entry.error && <div className="mt-1 text-[10px] text-red-500">{entry.error}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress bar */}
        {uploadProgress && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Mengunggah...</span>
              <span>{uploadProgress.done} / {uploadProgress.total}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-500"
                style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Active toggle */}
        <div className="flex items-center gap-2.5">
          <Toggle
            checked={uploadForm.is_active}
            onChange={(v) => setUploadForm({ ...uploadForm, is_active: v })}
          />
          <span className="text-sm text-gray-700">Langsung aktifkan setelah diunggah</span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Btn type="button" variant="secondary" onClick={onClose}>
            {uploadProgress ? 'Tutup' : 'Batal'}
          </Btn>
          <Btn type="submit" disabled={uploadFiles.length === 0 || !!uploadProgress}>
            <RiUploadCloud2Line />{' '}
            {uploadProgress ? `${uploadProgress.done}/${uploadProgress.total}…` : 'Unggah Semua'}
          </Btn>
        </div>
      </form>
    </Modal>
  )
}
